import { graphql, navigate } from 'gatsby';
import React from 'react';

import BlogSeo from '../components/Blog/BlogSeo';
import CardListing from '../components/Blog/CardListing';
import DropDown from '../components/Common/DropDown';
import SmartLink from '../components/Common/SmartLink';
import Layout from '../components/Layouts/Layout';
import ContentContainer from '../components/PageSections/ContentContainer';
import PageSection from '../components/PageSections/PageSection';
import * as styles from './categoryListing.module.scss';

const CategoryListing = ({ data, location, pageContext }) => {
  const path = location.pathname;
  const isBlogHomepage = pageContext.isBlogHomepage;
  const categoryName = isBlogHomepage ? 'Blog' : data.category?.categoryName;
  const blogposts = isBlogHomepage ? data.allBlogPosts.nodes : data.category?.blogpost || [];
  const filterPosts = (array) => {
    // Filter out noFollow posts
    const posts = array.filter((post) => post.pageMetaData?.noFollow !== 'Yes');
    // Filter out duplicates
    const seenIds = new Set();
    return posts.filter(({ id }) => !seenIds.has(id) && seenIds.add(id));
  };
  const filteredPosts = filterPosts(blogposts);

  // Create a predefined order of category slugs to sort by
  const categoryOrder = ['blog', 'insights', 'deep-dives', 'use-cases', 'updates', 'engineering', 'culture'];

  let categories = data.allCategories?.nodes?.map((category) => {
    return {
      label: category.categoryName,
      value: category.slug,
    };
  });

  categories.unshift({ label: 'All', value: 'blog' });
  categories.sort((a, b) => categoryOrder.indexOf(a.value) - categoryOrder.indexOf(b.value));

  const setCategory = (slug) => {
    slug === 'blog' ? navigate(`/blog/`) : navigate(`/blog/tag/${slug}/`);
  };

  const currentDDPlaceholder = isBlogHomepage ? 'All' : categories.find((opt) => opt.value === pageContext.slug)?.label;

  const categoryList = categories?.map((category, i) => {
    const url = category.label === 'All' ? `/blog/` : `/blog/tag/${category.value}/`;
    let isActive = path.includes(url);
    if (!isBlogHomepage && category.label === 'All') isActive = false;

    return (
      <SmartLink
        data-analytics-id={category.label}
        key={i}
        className={[isActive ? styles.active : ''].join(' ')}
        to={url}
      >
        {category.label}
      </SmartLink>
    );
  });

  return (
    <Layout isBlog className={styles.blog}>
      <PageSection isTopSection={true} slim className={styles.header}>
        <h1>{categoryName}</h1>
        <div className={styles.categoriesDropdown}>
          <DropDown
            aria-label={currentDDPlaceholder}
            placeholder={currentDDPlaceholder}
            options={categories}
            noOptionsMessage={() => 'Select category'}
            onChange={(choice) => setCategory(choice?.value)}
          />
        </div>
        <div className={styles.categoryGrid} data-analytics-component="blog-categories">
          {categoryList}
        </div>
      </PageSection>
      {filteredPosts && (
        <ContentContainer>
          <CardListing posts={filteredPosts} category={categoryName} sortByPublish={true} />
        </ContentContainer>
      )}
    </Layout>
  );
};

export const Head = ({ data, location, pageContext }) => (
  <BlogSeo
    slug={location.pathname}
    meta={{ pageTitle: pageContext.isBlogHomepage ? 'Blog' : `${data.category?.categoryName || 'Blog'}` }}
  />
);

export default CategoryListing;

export const query = graphql`
  query getCategory($slug: String!, $isBlogHomepage: Boolean!) {
    allBlogPosts: allContentfulPageBlogPost(
      filter: { node_locale: { eq: "en-US" }, pageMetaData: { hideOnCategoryLists: { ne: true } } }
    ) @include(if: $isBlogHomepage) {
      nodes {
        ...BlogPostSlim
      }
    }
    category: contentfulTaxCategory(slug: { eq: $slug }) @skip(if: $isBlogHomepage) {
      slug
      categoryName
      blogpost: page__blog_post {
        ...BlogPostSlim
      }
    }
    allCategories: allContentfulTaxCategory(filter: { node_locale: { eq: "en-US" } }) {
      nodes {
        slug
        categoryName
      }
    }
  }
`;
